/**
 * History Service
 * Handles all historical data queries and program archiving
 */
import { supabase } from '../supabase';
import { weeksInARow, longestWeeksInARow } from '../utils/weekStreak';
import { getSetVolume } from '../utils/weightUtils';
import { evaluateSetPR, summarisePriorSets, getRepRangeCategory } from './prService';
import * as Sentry from '@sentry/react';
import { dayTitle } from '../utils/dayTitle';

/**
 * Archive a program (soft delete)
 * @param {number} programId - Program ID to archive
 * @param {string} userId - User performing the action
 */
export const archiveProgram = async (programId, userId) => {
  const { error } = await supabase.rpc('archive_program', {
    p_program_id: programId,
    p_user_id: userId,
  });

  if (error) throw error;
};

/**
 * Unarchive a program
 * @param {number} programId - Program ID to restore
 */
export const unarchiveProgram = async (programId) => {
  const { error } = await supabase.rpc('unarchive_program', {
    p_program_id: programId,
  });

  if (error) throw error;
};

/**
 * End a program assignment when reassigning
 * @param {string} assignmentId - Assignment ID to end
 * @param {string} userId - User performing the action
 */
export const endProgramAssignment = async (assignmentId, userId) => {
  const { error } = await supabase.rpc('end_program_assignment', {
    p_assignment_id: assignmentId,
    p_user_id: userId,
  });

  if (error) throw error;
};

/**
 * Get all program assignments for a user (including historical)
 * @param {string} userId - User ID
 * @param {boolean} activeOnly - If true, only return active assignment
 */
export const getUserProgramAssignments = async (userId, activeOnly = false) => {
  let query = supabase
    .from('program_assignments')
    .select(`
      id,
      program_id,
      assigned_at,
      ended_at,
      is_active,
      programs (
        id,
        name,
        description,
        archived_at
      )
    `)
    .eq('user_id', userId)
    .order('assigned_at', { ascending: false });

  if (activeOnly) {
    query = query.eq('is_active', true);
  }

  const { data, error } = await query;

  if (error) throw error;
  // Filter out assignments whose program was hard-deleted (join returns null/undefined)
  return (data || []).filter(a => a.programs != null);
};

/**
 * Get complete exercise history across all programs
 * @param {string} userId - User ID
 * @param {number} exerciseId - Exercise ID
 * @param {number} limit - Max number of records
 * @param {string} [exerciseName] - Exercise name used as fallback if exercise_id returns nothing
 */
export const getExerciseHistory = async (userId, exerciseId, limit = 50, exerciseName = null) => {
  const selectClause = `
      set_number,
      weight,
      reps,
      rir,
      session_id,
      created_at,
      workout_sessions (
        started_at,
        completed_at,
        programs (
          name
        )
      )
    `;

  let data, error;

  if (exerciseId) {
    const result = await supabase
      .from('workout_logs')
      .select(selectClause)
      .eq('user_id', userId)
      .eq('exercise_id', exerciseId)
      .eq('completed', true)
      .order('created_at', { ascending: false })
      .limit(limit);
    data = result.data;
    error = result.error;
  }

  // Fallback: old logs may have exercise_id = null if the column was added after they were logged
  if ((!data || data.length === 0) && !error && exerciseName) {
    const result = await supabase
      .from('workout_logs')
      .select(selectClause)
      .eq('user_id', userId)
      .eq('exercise_name', exerciseName)
      .eq('completed', true)
      .order('created_at', { ascending: false })
      .limit(limit);
    data = result.data;
    error = result.error;
  }

  if (error) throw error;
  if (!data) return [];

  return data.map(log => ({
    session_id: log.session_id,
    session_date: log.workout_sessions?.started_at || log.workout_sessions?.completed_at || log.created_at,
    program_name: log.workout_sessions?.programs?.name || 'Unknown',
    set_number: log.set_number,
    weight: log.weight,
    reps: log.reps,
    rir: log.rir,
  }));
};

/**
 * Get statistics for a program
 * @param {number} programId - Program ID
 * @param {string} userId - User ID
 */
export const getProgramStats = async (programId, userId) => {
  const { data, error } = await supabase.rpc('get_program_stats', {
    p_program_id: programId,
    p_user_id: userId,
  });

  if (error) throw error;
  return data?.[0] || null;
};

/**
 * Get last workout for an exercise across ALL programs (not just current)
 * @param {string} userId - User ID
 * @param {number} exerciseId - Exercise ID
 * @param {string} [exerciseName] - Exercise name used as fallback if exercise_id returns nothing
 */
export const getLastWorkoutForExercise = async (userId, exerciseId, exerciseName = null) => {
  // Fetch exercise flags so we can pick the right "best set" metric
  const { data: exerciseInfo } = exerciseId
    ? await supabase.from('exercises').select('is_bodyweight, is_assisted').eq('id', exerciseId).maybeSingle()
    : { data: null };

  const isBodyweight = exerciseInfo?.is_bodyweight || false;
  const isAssisted = exerciseInfo?.is_assisted || false;

  const selectClause = `
      weight,
      reps,
      rir,
      set_number,
      created_at,
      exercise_type,
      duration_seconds,
      intensity,
      workout_sessions (
        started_at,
        completed_at,
        programs (
          name
        )
      )
    `;

  let data, error;

  if (exerciseId) {
    const result = await supabase
      .from('workout_logs')
      .select(selectClause)
      .eq('user_id', userId)
      .eq('exercise_id', exerciseId)
      .eq('completed', true)
      .order('created_at', { ascending: false })
      .limit(10);
    data = result.data;
    error = result.error;
  }

  // Fallback: old logs may have exercise_id = null if the column was added after they were logged
  if ((!data || data.length === 0) && !error && exerciseName) {
    const result = await supabase
      .from('workout_logs')
      .select(selectClause)
      .eq('user_id', userId)
      .eq('exercise_name', exerciseName)
      .eq('completed', true)
      .order('created_at', { ascending: false })
      .limit(10);
    data = result.data;
    error = result.error;
  }

  if (error) throw error;

  // Group by session and get the best set
  if (data && data.length > 0) {
    const sessions = {};
    data.forEach(log => {
      const sessionDate = log.workout_sessions?.completed_at || log.workout_sessions?.started_at || log.created_at;
      if (!sessions[sessionDate]) {
        sessions[sessionDate] = {
          date: sessionDate,
          program: log.workout_sessions?.programs?.name || null,
          exercise_type: log.exercise_type,
          sets: [],
        };
      }
      sessions[sessionDate].sets.push({
        weight: log.weight,
        reps: log.reps,
        rir: log.rir,
        set_number: log.set_number,
        duration_seconds: log.duration_seconds,
        intensity: log.intensity,
      });
    });

    // Get most recent session
    const mostRecent = Object.values(sessions)[0];
    if (mostRecent) {
      let bestSet;
      if (mostRecent.exercise_type === 'cardio' || mostRecent.exercise_type === 'stretch') {
        // Cardio/stretch: longest duration
        bestSet = mostRecent.sets.reduce((best, current) => {
          return (current.duration_seconds || 0) > (best.duration_seconds || 0) ? current : best;
        }, mostRecent.sets[0]);
      } else if (isAssisted) {
        // Assisted: lowest weight = least assistance = hardest = best set
        bestSet = mostRecent.sets.reduce((best, current) => {
          const currentW = current.weight ?? Infinity;
          const bestW = best.weight ?? Infinity;
          return currentW < bestW ? current : best;
        }, mostRecent.sets[0]);
      } else if (isBodyweight) {
        // Bodyweight: logged weight tracks body composition, not performance — use most reps
        bestSet = mostRecent.sets.reduce((best, current) => {
          return (current.reps || 0) > (best.reps || 0) ? current : best;
        }, mostRecent.sets[0]);
      } else {
        // Regular resistance: highest volume (weight × reps)
        bestSet = mostRecent.sets.reduce((best, current) => {
          const currentVolume = (current.weight || 0) * (current.reps || 0);
          const bestVolume = (best.weight || 0) * (best.reps || 0);
          return currentVolume > bestVolume ? current : best;
        }, mostRecent.sets[0]);
      }

      return {
        ...bestSet,
        date: mostRecent.date,
        program: mostRecent.program,
        exercise_type: mostRecent.exercise_type,
      };
    }
  }

  return null;
};

/**
 * Get workout history for a date range
 * @param {string} userId - User ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 */
export const getWorkoutHistory = async (userId, startDate, endDate) => {
  const { data, error } = await supabase
    .from('workout_sessions')
    .select(`
      id,
      started_at,
      completed_at,
      rpe,
      notes,
      programs (
        id,
        name
      ),
      program_days (
        name,
        day_name,
        day_number
      )
    `)
    .eq('user_id', userId)
    .not('completed_at', 'is', null)
    .gte('started_at', startDate.toISOString())
    .lte('started_at', endDate.toISOString())
    .order('completed_at', { ascending: false });

  if (error) throw error;
  return data;
};

/**
 * Get all archived programs for a user
 * @param {string} userId - User ID
 */
export const getArchivedPrograms = async (userId) => {
  // Get all program IDs user has been assigned to
  const { data: assignments } = await supabase
    .from('program_assignments')
    .select('program_id')
    .eq('user_id', userId);

  if (!assignments || assignments.length === 0) return [];

  const programIds = assignments.map(a => a.program_id);

  const { data, error } = await supabase
    .from('programs')
    .select('*')
    .in('id', programIds)
    .not('archived_at', 'is', null)
    .order('archived_at', { ascending: false });

  if (error) throw error;
  return data;
};

/**
 * Get lifetime statistics for a user
 * @param {string} userId - User ID
 */
export const getLifetimeStats = async (userId) => {
  // Each read that fails throws: a failure must not read as "no workouts yet" (Codex's task 017 review)
  // Total workouts completed (sessions with at least one completed log)
  const { data: completedSessionIds, error: sessionsError } = await supabase
    .from('workout_logs')
    .select('session_id')
    .eq('user_id', userId)
    .eq('completed', true)
    .limit(10000);
  if (sessionsError) throw sessionsError;
  const totalWorkouts = new Set(completedSessionIds?.map(l => l.session_id) || []).size;

  // Total sets completed
  const { count: totalSets, error: setsError } = await supabase
    .from('workout_logs')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('completed', true);
  if (setsError) throw setsError;

  // Current streak: weeks in a row with a completed session, the one rule every screen uses
  const { current: currentStreak } = await getWeekStreak(userId);

  // Fetch user bodyweight for effective volume calculation
  const { data: userData } = await supabase
    .from('users')
    .select('bodyweight_kg')
    .eq('auth_user_id', userId)
    .single();
  const userBodyweight = userData?.bodyweight_kg || 0;

  // Total volume (effective weight x reps) - accounts for bodyweight/assisted exercises
  const { data: volumeData, error: volumeError } = await supabase
    .from('workout_logs')
    .select('weight, reps, exercise_id')
    .eq('user_id', userId)
    .eq('completed', true)
    .not('reps', 'is', null)
    .limit(10000);
  if (volumeError) throw volumeError;

  // Get exercise details for bodyweight calculations
  const volumeExerciseIds = [...new Set(volumeData?.map(l => l.exercise_id).filter(Boolean) || [])];
  let volumeExerciseMap = new Map();
  if (volumeExerciseIds.length > 0) {
    const { data: volumeExercises, error: exercisesError } = await supabase
      .from('exercises')
      .select('id, is_bodyweight, is_assisted, bodyweight_multiplier')
      .in('id', volumeExerciseIds);
    if (exercisesError) throw exercisesError;
    volumeExerciseMap = new Map(volumeExercises?.map(e => [e.id, e]) || []);
  }

  const totalVolume = volumeData?.reduce((sum, log) => {
    const exercise = volumeExerciseMap.get(log.exercise_id);
    return sum + getSetVolume(log.weight, log.reps, exercise, userBodyweight);
  }, 0) || 0;

  // First workout date
  const { data: firstWorkout } = await supabase
    .from('workout_sessions')
    .select('started_at, completed_at')
    .eq('user_id', userId)
    .order('started_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  // Recent programs count
  const { data: programs } = await supabase
    .from('program_assignments')
    .select('program_id')
    .eq('user_id', userId);

  return {
    totalWorkouts: totalWorkouts || 0,
    totalSets: totalSets || 0,
    totalVolume: Math.round(totalVolume),
    firstWorkoutDate: firstWorkout?.completed_at || firstWorkout?.started_at || null,
    totalPrograms: programs?.length || 0,
    currentStreak,
  };
};

/**
 * The streak every screen shows (Stats, the calendar, the session summary): weeks in a row with a session
 * finished with Complete Day, dated by when it was finished; a day marked missed does not count (Codex's
 * reviews of the client's Stats, tasks 017 M3 and 018 R1). { current, longest }, both in weeks. The last
 * 400 completed sessions are enough for any streak a programme of up to seven sessions a week can hold.
 */
export const getWeekStreak = async (userId) => {
  const { data: sessions, error } = await supabase
    .from('workout_sessions')
    .select('completed_at')
    .eq('user_id', userId)
    .eq('status', 'completed')
    .not('completed_at', 'is', null)
    .order('completed_at', { ascending: false })
    .limit(400);
  if (error || !sessions?.length) return { current: 0, longest: 0 };
  const finished = sessions.map(s => s.completed_at);
  return { current: weeksInARow(finished), longest: longestWeeksInARow(finished) };
};

/**
 * Get this week's training snapshot (Monday-based week)
 * @param {string} userId - User ID
 * @returns {{ sessions: number, volume: number }} sessions: workouts completed this week (Complete Day),
 *   not days trained: two sessions on one day are two (task 017, M3)
 */
export const getThisWeekSnapshot = async (userId) => {
  const now = new Date();
  const weekStart = new Date(now);
  const day = weekStart.getDay();
  weekStart.setDate(weekStart.getDate() - (day === 0 ? 6 : day - 1)); // Monday
  weekStart.setHours(0, 0, 0, 0);

  // Query completed logs directly — avoids session_id linkage gaps
  const { data: logs } = await supabase
    .from('workout_logs')
    .select('created_at, weight, reps, exercise_id')
    .eq('user_id', userId)
    .eq('completed', true)
    .gte('created_at', weekStart.toISOString())
    .not('reps', 'is', null);

  // Sessions completed this week: completed with Complete Day, not marked missed
  const { data: doneSessions } = await supabase
    .from('workout_sessions')
    .select('id')
    .eq('user_id', userId)
    .eq('status', 'completed')
    .gte('completed_at', weekStart.toISOString());
  const sessions = (doneSessions || []).length;

  if (!logs || logs.length === 0) return { sessions, volume: 0 };

  const { data: userData } = await supabase
    .from('users')
    .select('bodyweight_kg')
    .eq('auth_user_id', userId)
    .single();
  const userBodyweight = userData?.bodyweight_kg || 0;

  const exerciseIds = [...new Set(logs.map(l => l.exercise_id).filter(Boolean))];
  let exerciseMap = new Map();
  if (exerciseIds.length > 0) {
    const { data: exercises } = await supabase
      .from('exercises')
      .select('id, is_bodyweight, is_assisted, bodyweight_multiplier')
      .in('id', exerciseIds);
    exerciseMap = new Map(exercises?.map(e => [e.id, e]) || []);
  }

  const totalVolume = logs.reduce((sum, log) => {
    const exercise = exerciseMap.get(log.exercise_id);
    return sum + getSetVolume(log.weight, log.reps, exercise, userBodyweight);
  }, 0);

  return { sessions, volume: Math.round(totalVolume) };
};

/**
 * Detect new personal records in a session
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @returns {Array} Array of PRs achieved in this session
 */
export const detectSessionPRs = async (sessionId, userId) => {
  try {
    // No bodyweight lookup: rep-range PRs compare the RAW logged weight, exactly as prService
    // does, and bodyweight exercises are excluded from PR detection altogether (their logged
    // load moves with body composition, not performance). One fewer query per summary.

    // Get all logs from this session with exercise details. Weight-based PRs only
    // apply to logged sets that actually have a weight and rep count — cardio/stretch
    // exercises log duration/intensity instead and have weight=null, reps=null, which
    // would otherwise surface as a hollow "0kg x 0" PR the first time one is logged.
    const { data: rawSessionLogs, error: logsError } = await supabase
      .from('workout_logs')
      .select('id, weight, reps, exercise_id, created_at')
      .eq('session_id', sessionId)
      .eq('completed', true);

    if (logsError) throw logsError;
    const sessionLogs = (rawSessionLogs || []).filter(log => log.weight != null && log.reps != null);
    if (sessionLogs.length === 0) return [];

    // Get exercise details including bodyweight flags
    const exerciseIds = [...new Set(sessionLogs.map(log => log.exercise_id))];
    const { data: exercises, error: exError } = await supabase
      .from('exercises')
      .select('id, name, is_bodyweight, is_assisted, bodyweight_multiplier')
      .in('id', exerciseIds);

    if (exError) throw exError;

    // Keyed by String(id): exerciseGroups below is a plain object, so its keys
    // (and Object.entries(...) keys read from it) are always strings even though
    // exercise_id is numeric — a Map keyed by the raw numeric id would silently
    // never match, making every lookup fail and every PR show as "Unknown".
    const exerciseMap = new Map(exercises?.map(e => [String(e.id), e]) || []);

    // Group logs by exercise
    const exerciseGroups = {};
    sessionLogs.forEach(log => {
      if (!exerciseGroups[log.exercise_id]) {
        exerciseGroups[log.exercise_id] = [];
      }
      exerciseGroups[log.exercise_id].push(log);
    });

    // Find each exercise's best set in this session (and its timestamp, which
    // bounds how far back that exercise's historical-PR lookup should go).
    // Judge every set in the session, oldest first, against the SAME rules the live badge and
    // the Stats screens use — evaluateSetPR in prService. This used to score sets by volume
    // (weight x reps), a third definition that disagreed with both: 95kg x 15 "beat" 115kg x 10
    // because 1425 > 1150, so training lighter was celebrated as a +24% record.
    Object.values(exerciseGroups).forEach(logs =>
      logs.sort((a, b) => String(a.created_at).localeCompare(String(b.created_at))));

    // One batched historical query for every exercise in this session, instead of
    // one query per exercise. Each exercise still needs its own cutoff (the
    // timestamp of its own best set), so fetch everything before the latest of
    // those cutoffs and filter each exercise's slice back down in memory below.
    // Everything logged before this session's LAST set; each exercise is narrowed to its own
    // set's timestamp below, so a set is never judged against something logged after it.
    const cutoff = sessionLogs
      .reduce((max, log) => String(log.created_at) > max ? String(log.created_at) : max, '');

    const { data: allHistoricalLogs, error: histError } = await supabase
      .from('workout_logs')
      .select('weight, reps, exercise_id, created_at')
      .eq('user_id', userId)
      .in('exercise_id', exerciseIds)
      .eq('completed', true)
      .lt('created_at', cutoff);
    // No per-call limit: unlike the admin list-view pagination fixed elsewhere this
    // session, this is scoped to one user's history on the handful of exercises in
    // a single workout — it can't grow with the overall user base, and a shared
    // limit across exercises risked starving whichever exercise's rows the DB
    // happened to return last (the original per-exercise .limit(100) didn't have
    // that risk since each exercise got its own query and its own cap).

    if (histError) {
      Sentry.captureException(histError, { extra: { fn: 'detectSessionPRs', sessionId } });
      console.error('Error fetching historical data:', histError);
      return [];
    }

    const historicalByExercise = {};
    (allHistoricalLogs || []).forEach(log => {
      if (!historicalByExercise[log.exercise_id]) historicalByExercise[log.exercise_id] = [];
      historicalByExercise[log.exercise_id].push(log);
    });

    const prs = [];

    for (const [exerciseId, logs] of Object.entries(exerciseGroups)) {
      const exercise = exerciseMap.get(exerciseId);
      const history = historicalByExercise[exerciseId] || [];

      // Walk the session's sets in order, adding each to what counts as "already done". That
      // mirrors the live badge: set 2 is judged against set 1, so three identical sets award one
      // record, and a warm-up followed by a top set awards it to the top set.
      const seen = [];
      let winner = null;
      for (const log of logs) {
        const priorSets = history
          .filter(h => String(h.created_at) < String(log.created_at))
          .concat(seen);
        const verdict = evaluateSetPR({
          weight: log.weight,
          reps: log.reps,
          isBodyweight: exercise?.is_bodyweight || false,
          isAssisted: exercise?.is_assisted || false,
          ...summarisePriorSets(priorSets, {
            weight: log.weight,
            reps: log.reps,
            isAssisted: exercise?.is_assisted || false,
          }),
        });
        // One record per exercise on the summary, and it should be the most impressive one.
        // NOT simply the last: rep ranges are independent ladders, so a first-ever 60kg x 12
        // can land after a 120kg x 10 record without being the better lift. Heaviest wins —
        // or, for assisted work, the set that needed the least help.
        if (verdict.isPR) {
          const better = !winner
            || (exercise?.is_assisted
              ? parseFloat(log.weight) < parseFloat(winner.log.weight)
              : parseFloat(log.weight) > parseFloat(winner.log.weight));
          if (better) winner = { log, verdict };
        }
        seen.push(log);
      }

      if (!winner) continue;
      const { log, verdict } = winner;
      prs.push({
        exerciseId,
        exerciseName: exercise?.name || 'Unknown',
        weight: log.weight,
        reps: log.reps,
        repRange: getRepRangeCategory(log.reps),
        isFirstTime: verdict.isFirstEver || false,
        // Kilograms, not a percentage. The old "+23.9%" was a ratio of two volume figures and
        // meant nothing a lifter could act on.
        improvement: verdict.improvement != null
          ? `+${Math.round(verdict.improvement * 10) / 10}kg`
          : null,
        previousBest: verdict.previousPR
          ? { weight: verdict.previousPR.weight, reps: verdict.previousPR.reps }
          : null,
      });
    }

    return prs;
  } catch (error) {
    Sentry.captureException(new Error(error?.message ?? String(error)), { extra: { fn: 'detectSessionPRs', supabaseError: error, sessionId, userId } });
    console.error('Error detecting PRs:', error);
    return [];
  }
};

/**
 * Get metrics for a specific session
 * @param {string} sessionId - Session ID
 * @returns {Object} Session metrics
 */
export const getSessionMetrics = async (sessionId, userId = null) => {
  try {
    const { data: logs, error: logsError } = await supabase
      .from('workout_logs')
      .select('weight, reps, exercise_id')
      .eq('session_id', sessionId)
      .eq('completed', true);

    if (logsError) throw logsError;

    // Get user bodyweight if userId provided
    let userBodyweight = 0;
    if (userId) {
      const { data: userData } = await supabase
        .from('users')
        .select('bodyweight_kg')
        .eq('auth_user_id', userId)
        .single();
      userBodyweight = userData?.bodyweight_kg || 0;
    }

    // Get exercise details for bodyweight/assisted calculations
    const exerciseIds = [...new Set(logs?.map(log => log.exercise_id) || [])];
    let exerciseMap = new Map();

    if (exerciseIds.length > 0) {
      const { data: exercises } = await supabase
        .from('exercises')
        .select('id, is_bodyweight, is_assisted, bodyweight_multiplier')
        .in('id', exerciseIds);

      exerciseMap = new Map(exercises?.map(ex => [ex.id, ex]) || []);
    }

    const totalVolume = logs?.reduce((sum, log) => {
      const exercise = exerciseMap.get(log.exercise_id);
      return sum + getSetVolume(log.weight, log.reps, exercise, userBodyweight);
    }, 0) || 0;

    const totalReps = logs?.reduce((sum, log) => sum + (log.reps || 0), 0) || 0;
    const totalSets = logs?.length || 0;

    const { data: session, error: sessionError } = await supabase
      .from('workout_sessions')
      .select('started_at, completed_at, user_id')
      .eq('id', sessionId)
      .single();

    if (sessionError) throw sessionError;

    let duration = 0;
    if (session && session.started_at && session.completed_at) {
      const diffMs = new Date(session.completed_at) - new Date(session.started_at);
      duration = Math.floor(diffMs / 60000); // Convert to minutes
    }

    return {
      totalVolume: Math.round(totalVolume),
      totalReps,
      totalSets,
      duration,
    };
  } catch (error) {
    Sentry.captureException(new Error(error?.message ?? String(error)), { extra: { fn: 'getSessionMetrics', supabaseError: error, sessionId } });
    console.error('Error getting session metrics:', error);
    return {
      totalVolume: 0,
      totalReps: 0,
      totalSets: 0,
      duration: 0,
    };
  }
};

/**
 * Get last similar workout for comparison
 * Strategy: 1) Same day_id, 2) Similar muscle groups
 * @param {string} currentSessionId - Current session ID
 * @param {string} userId - User ID
 * @returns {Object} Comparison data or null
 */
export const getLastSimilarWorkout = async (currentSessionId, userId) => {
  try {
    // Get current session data
    const { data: currentSession, error: currentError } = await supabase
      .from('workout_sessions')
      .select('id, day_id, completed_at')
      .eq('id', currentSessionId)
      .single();

    if (currentError) throw currentError;

    // Strategy 1: the same workout day, in any week of the same program.
    //
    // This used to match on day_id alone. Every generated week creates its OWN program_days
    // rows, so this week's Day 1 and last week's Day 1 have different ids and the match
    // effectively never fired across weeks. The comparison then fell through to Strategy 2's
    // loose "similar muscle groups" match, which would happily hold a 30-set session up
    // against a 15-set one and report swings like +211%. Matching on day_number within the
    // program gave the like-for-like, until "Add a day" (migration 087) could move a day to
    // another number in later weeks: the day's identity (slot_key, 086) is the match now, and
    // the number only for a day without one (Codex's review 032, R3).
    if (currentSession.day_id) {
      const { data: currentDay } = await supabase
        .from('program_days')
        .select('day_number, slot_key, name, day_name, week_id, program_weeks(program_id)')
        .eq('id', currentSession.day_id)
        .single();

      const programId = currentDay?.program_weeks?.program_id;
      let comparableDayIds = [currentSession.day_id];

      if (programId && (currentDay?.slot_key || currentDay?.day_number != null)) {
        const { data: weeks } = await supabase
          .from('program_weeks')
          .select('id')
          .eq('program_id', programId);
        const weekIds = (weeks || []).map(w => w.id);

        if (weekIds.length > 0) {
          const { data: days } = await supabase
            .from('program_days')
            .select('id')
            .eq(...(currentDay.slot_key ? ['slot_key', currentDay.slot_key] : ['day_number', currentDay.day_number]))
            .in('week_id', weekIds);
          if (days?.length) comparableDayIds = days.map(d => d.id);
        }
      }

      const { data: sameDaySession, error: sameDayError } = await supabase
        .from('workout_sessions')
        .select('id, started_at, completed_at')
        .eq('user_id', userId)
        .in('day_id', comparableDayIds)
        .neq('id', currentSessionId)
        // Skip sessions that were started and abandoned: an empty one has no logged sets, so
        // comparing against it reports a meaningless 0 for every metric.
        .not('completed_at', 'is', null)
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!sameDayError && sameDaySession) {
        const metrics = await getSessionMetrics(sameDaySession.id, userId);

        return {
          session: sameDaySession,
          metrics,
          matchType: 'same_day',
          dayName: dayTitle(currentDay) || 'Previous Workout',
        };
      }
    }

    // Strategy 2: Find session with similar muscle groups
    // Get muscle groups for current session
    const { data: currentLogs } = await supabase
      .from('workout_logs')
      .select('exercise_id')
      .eq('session_id', currentSessionId)
      .eq('completed', true);

    if (!currentLogs || currentLogs.length === 0) return null;

    const currentExerciseIds = [...new Set(currentLogs.map(log => log.exercise_id))];

    const { data: currentExercises } = await supabase
      .from('exercises')
      .select('movement_category')
      .in('id', currentExerciseIds);

    const currentMuscles = new Set(currentExercises?.map(e => e.movement_category) || []);

    // Get recent sessions to compare
    const { data: recentSessions } = await supabase
      .from('workout_sessions')
      .select('id, started_at, completed_at')
      .eq('user_id', userId)
      .neq('id', currentSessionId)
      .order('started_at', { ascending: false })
      .limit(10);

    if (!recentSessions || recentSessions.length === 0) return null;

    // Check each session for muscle overlap — batch-fetch all logs at once (avoids N+1)
    const recentSessionIds = recentSessions.map(s => s.id);
    const { data: allRecentLogs } = await supabase
      .from('workout_logs')
      .select('session_id, exercise_id')
      .in('session_id', recentSessionIds)
      .eq('completed', true);

    // Group logs by session_id in memory
    const logsBySession = {};
    allRecentLogs?.forEach(log => {
      if (!logsBySession[log.session_id]) logsBySession[log.session_id] = [];
      logsBySession[log.session_id].push(log);
    });

    // Collect all unique exercise IDs across all recent sessions for a single exercises fetch
    const allRecentExerciseIds = [...new Set(allRecentLogs?.map(log => log.exercise_id) || [])];
    let recentExerciseMap = new Map();
    if (allRecentExerciseIds.length > 0) {
      const { data: recentExercises } = await supabase
        .from('exercises')
        .select('id, movement_category')
        .in('id', allRecentExerciseIds);
      recentExerciseMap = new Map(recentExercises?.map(e => [e.id, e]) || []);
    }

    for (const session of recentSessions) {
      const sessionLogs = logsBySession[session.id] || [];
      if (sessionLogs.length === 0) continue;

      const exerciseIds = [...new Set(sessionLogs.map(log => log.exercise_id))];
      const sessionMuscles = new Set(
        exerciseIds.map(id => recentExerciseMap.get(id)?.movement_category).filter(Boolean)
      );

      // Calculate overlap
      const intersection = new Set([...currentMuscles].filter(m => sessionMuscles.has(m)));
      const overlap = intersection.size / Math.max(currentMuscles.size, sessionMuscles.size);

      if (overlap >= 0.5) {
        // 50% overlap threshold
        const metrics = await getSessionMetrics(session.id, userId);
        return {
          session,
          metrics,
          matchType: 'similar_muscles',
          overlap: Math.round(overlap * 100),
        };
      }
    }

    return null;
  } catch (error) {
    Sentry.captureException(new Error(error?.message ?? String(error)), { extra: { fn: 'getLastSimilarWorkout', supabaseError: error, currentSessionId, userId } });
    console.error('Error finding similar workout:', error);
    return null;
  }
};

/**
 * Search workout logs with filters
 * @param {string} userId - User ID
 * @param {object} filters - Filter options
 */
export const searchWorkoutLogs = async (userId, filters = {}) => {
  let query = supabase
    .from('workout_logs')
    .select(`
      *,
      exercises (
        name,
        muscle_group
      ),
      workout_sessions!inner (
        started_at,
        completed_at,
        programs (
          name
        )
      )
    `)
    .eq('user_id', userId)
    .eq('completed', true);

  if (filters.exerciseId) {
    query = query.eq('exercise_id', filters.exerciseId);
  }

  if (filters.startDate) {
    query = query.gte('created_at', filters.startDate);
  }

  if (filters.endDate) {
    query = query.lte('created_at', filters.endDate);
  }

  if (filters.programId) {
    query = query.eq('workout_sessions.program_id', filters.programId);
  }

  query = query.order('created_at', { ascending: false });

  if (filters.limit) {
    query = query.limit(filters.limit);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data;
};
